import { useState } from "react"

import { MAX_MULTI_RUN_IDS, useMultiRunMetadataTransport } from "@/runs"
import { Button } from "@coval/ui"
import { BotMessageSquare, MessagesSquare } from "lucide-react"

import { MessageSquareArrowUp } from "@/components/icons/MessageSquareArrowUp"

export function ReportConversationSummary({ runIds }: { runIds: string[] }) {
  const [visibleBatches, setVisibleBatches] = useState(1)
  const sourceIds = [...new Set(runIds.map((id) => id.trim()).filter(Boolean))]
  const visibleCount = Math.min(
    sourceIds.length,
    visibleBatches * MAX_MULTI_RUN_IDS,
  )
  return (
    <div className="min-w-0 space-y-3">
      {Array.from(
        { length: Math.ceil(visibleCount / MAX_MULTI_RUN_IDS) },
        (_, index) => (
          <ReportConversationBatch
            key={index}
            runIds={sourceIds.slice(
              index * MAX_MULTI_RUN_IDS,
              (index + 1) * MAX_MULTI_RUN_IDS,
            )}
          />
        ),
      )}
      {visibleCount < sourceIds.length ? (
        <div className="space-y-1">
          <p className="text-xs text-coval-text-muted">
            Showing {visibleCount.toLocaleString()} of{" "}
            {sourceIds.length.toLocaleString()} sources.
          </p>
          <Button
            variant="ghost"
            onClick={() => setVisibleBatches((count) => count + 1)}
          >
            Show more sources
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function ReportConversationBatch({ runIds }: { runIds: string[] }) {
  const { data, error, isLoading, refetch } = useMultiRunMetadataTransport(
    runIds,
    { staleTime: 2 * 60 * 1000 },
  )
  const runsById = new Map(data?.runs.map((run) => [run.id, run]))

  return (
    <div className="min-w-0 space-y-2">
      <ul className="space-y-3">
        {runIds.map((id) => {
          const run = runsById.get(id)
          const Icon = run
            ? run.is_monitoring
              ? MessageSquareArrowUp
              : BotMessageSquare
            : MessagesSquare
          const sourceLabel = run
            ? run.is_monitoring
              ? "Uploaded conversations"
              : "Simulation run"
            : "Conversations"
          const name = run?.display_name?.trim()
          const count = run?.progress?.total_size
          // Older runs serialize missing progress as zero; do not present it as an empty run.
          const hasCount =
            typeof count === "number" && Number.isInteger(count) && count > 0
          const details = `${sourceLabel}${hasCount ? ` · ${count.toLocaleString()} ${count === 1 ? "conversation" : "conversations"}` : ""}`
          return (
            <li key={id} className="flex min-w-0 items-start gap-2">
              <Icon
                className="mt-0.5 size-4 shrink-0 text-coval-text-muted"
                aria-hidden="true"
              />
              <div className="min-w-0 space-y-0.5">
                <p className="break-words text-sm text-coval-text">
                  {name || details}
                </p>
                {name ? (
                  <p className="text-xs text-coval-text-muted">{details}</p>
                ) : null}
                <p className="break-all font-mono text-xs text-coval-text-muted">
                  {id}
                </p>
                {!run ? (
                  <p className="text-xs text-coval-text-muted">
                    {error
                      ? "Details unavailable"
                      : isLoading
                        ? "Loading details…"
                        : "Details unavailable"}
                  </p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ul>
      {error ? (
        <Button variant="ghost" onClick={() => void refetch()}>
          Retry details
        </Button>
      ) : null}
    </div>
  )
}
